import { type PersistedResourceIdentifier } from '@opallabs/json-api'
import type { EffectivePrivacy } from '../PrivacyState'

type ReadonlyMetaField = {
  /** The name of the group or resource. */
  title: string
  /** The number of users who are associated with the group/resource.  */
  count: number
}

type UserMetadata = PersistedResourceIdentifier<'user'>
type GroupMetadata = PersistedResourceIdentifier<'user_group'>
type ResourceMetadata = PersistedResourceIdentifier & {
  meta: {
    /** True if access should be inherited by this resource's membership. */
    inherits_membership: boolean
  }
}

export type WritableAccessMetadata = {
  users?: UserMetadata[]
  groups?: GroupMetadata[]
  resources?: ResourceMetadata[]
}

export type ReadableAccessMetadata = {
  effectivePrivacy?: EffectivePrivacy | null
  users: UserMetadata[]
  groups: (GroupMetadata & { meta: ReadonlyMetaField })[]
  resources: (Omit<ResourceMetadata, 'meta'> & {
    meta: ResourceMetadata['meta'] & ReadonlyMetaField
  })[]
}

export type InheritanceResource = {
  type: 'resource'
  resourceType: string
  uuid: string
  title: string
  memberCount: number
  inheritsMembership: boolean
}

/** Convert `ReadableAccessMetadata.resources` to `InheritanceResource[]` */
export function readableResourcesAsInheritanceResources(
  resources: ReadableAccessMetadata['resources']
): InheritanceResource[]
export function readableResourcesAsInheritanceResources(
  resources: ReadableAccessMetadata['resources'] | undefined
): InheritanceResource[] | undefined
export function readableResourcesAsInheritanceResources(
  resources: ReadableAccessMetadata['resources'] | undefined
): InheritanceResource[] | undefined {
  return resources?.map((resource) => ({
    inheritsMembership: resource.meta.inherits_membership,
    memberCount: resource.meta.count,
    resourceType: resource.type,
    title: resource.meta.title,
    type: 'resource',
    uuid: resource.id,
  }))
}
